import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import EBS from '../components/EBS';
import AkademisyenTanimla from '../components/AkademisyenTanimla';
import AkademisyenTopluEkle from '../components/AkademisyenTopluEkle';
import AutoAssignCourses from '../components/AutoAssignCourses';
import RoleAssignment from '../components/RoleAssignment';
import ActiveUsers from '../components/ActiveUsers';
import AdminTermOutcomeFinalization from './AdminTermOutcomeFinalization';
import AdminAcademicYearAdvance from './AdminAcademicYearAdvance';
import AdminSidebar from './AdminSidebar';
import { getSelectedYear, isReadOnly } from '../config/academicYear';
import './AdminPanel.css';
import { API_ENDPOINTS } from '../config/api';

const AdminPanel = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('adminActiveTab') || 'ebs');
  const [talepler, setTalepler] = useState([]);
  const [loadingTalepler, setLoadingTalepler] = useState(false);
  const [errorTalepler, setErrorTalepler] = useState('');
  const [selectedTalep, setSelectedTalep] = useState(null);
  const [yanitlar, setYanitlar] = useState([]);
  const [loadingYanit, setLoadingYanit] = useState(false);
  const [yanitError, setYanitError] = useState('');
  const [yanitSuccess, setYanitSuccess] = useState('');
  const [yanitText, setYanitText] = useState('');
  const [resetRequests, setResetRequests] = useState([]);
  const [loadingReset, setLoadingReset] = useState(false);
  const [resetError, setResetError] = useState('');
  const [selectedReset, setSelectedReset] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetResult, setResetResult] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  const userRole = sessionStorage.getItem('userRole');
  const isAdmin = userRole === 'admin';
  const selectedAcademicYear = getSelectedYear();
  const archiveMode = isReadOnly();

  useEffect(() => {
    const userRole = sessionStorage.getItem('userRole');
    if (userRole !== 'admin') {
      navigate('/dashboard');
    }
  }, [navigate]);

  // Aktif sekmeyi oturum boyunca koru
  useEffect(() => {
    sessionStorage.setItem('adminActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'feedback') {
      setLoadingTalepler(true);
      setErrorTalepler('');
      fetch(API_ENDPOINTS.TALEP)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setTalepler(data.talepler);
          } else {
            setErrorTalepler(data.error || 'Talepler alınamadı.');
          }
        })
        .catch(() => setErrorTalepler('Sunucuya bağlanılamadı.'))
        .finally(() => setLoadingTalepler(false));
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'reset') {
      fetchResetRequests();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const fetchResetRequests = () => {
    setLoadingReset(true);
    setResetError('');
    fetch(API_ENDPOINTS.TALEP)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setResetRequests(data.talepler.filter(t => t.title && t.title.toLowerCase().includes('şifre sıfırlama')));
        } else {
          setResetError(data.error || 'Talepler alınamadı.');
        }
      })
      .catch(() => setResetError('Sunucuya bağlanılamadı.'))
      .finally(() => setLoadingReset(false));
  };

  const handleLogout = () => {
    sessionStorage.clear();
    document.cookie.split(';').forEach(function(c) {
      document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });
    window.location.href = '/';
  };

  const handleTalepClick = (talep) => {
    setSelectedTalep(talep);
    setLoadingYanit(true);
    setYanitError('');
    setYanitSuccess('');
    setYanitText('');
    fetch(`${API_ENDPOINTS.TALEP_YANIT}?talep_id=${talep.id}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setYanitlar(data.yanitlar);
        } else {
          setYanitlar([]);
        }
      })
      .catch(() => setYanitlar([]))
      .finally(() => setLoadingYanit(false));
  };

  const handleYanitSubmit = async (e) => {
    e.preventDefault();
    setYanitError('');
    setYanitSuccess('');
    if (!yanitText.trim()) {
      setYanitError(t('admin.replyEmpty'));
      return;
    }
    try {
      const res = await fetch(API_ENDPOINTS.TALEP_YANIT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          talep_id: selectedTalep.id,
          yanit: yanitText
        })
      });
      const data = await res.json();
      if (data.success) {
        setYanitSuccess(t('admin.replySaved'));
        setYanitText('');
        // Yanıtları tekrar çek
        fetch(`${API_ENDPOINTS.TALEP_YANIT}?talep_id=${selectedTalep.id}`)
          .then(res => res.json())
          .then(data => {
            if (data.success) setYanitlar(data.yanitlar);
          });
      } else {
        setYanitError(data.error || t('admin.replySaveFailed'));
      }
    } catch {
      setYanitError(t('admin.serverError'));
    }
  };

  const handleResetClick = (talep) => {
    setSelectedReset(talep);
    const match = talep.description && talep.description.match(/[\w.-]+@[\w.-]+\.[A-Za-z]{2,}/);
    setResetEmail(match ? match[0] : '');
    setNewPassword('');
    setResetResult('');
    setResetLoading(false);
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetResult('');
    setResetLoading(true);
    if (!resetEmail || !newPassword) {
      setResetResult(t('admin.emailRequired'));
      setResetLoading(false);
      return;
    }
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_RESET_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, new_password: newPassword })
      });
      const data = await res.json();
      if (data.success) {
        setResetResult(t('admin.passwordUpdated'));
        setTimeout(() => {
          setSelectedReset(null);
          fetchResetRequests();
        }, 1500);
      } else {
        setResetResult(data.error || t('admin.passwordUpdateFailed'));
      }
    } catch {
      setResetResult(t('admin.serverError'));
    }
    setResetLoading(false);
  };





  return (
    <>
      <Header />
      <div className="admin-panel-container">
        <AdminSidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          handleLogout={handleLogout} 
        />
        <main className="admin-main-content">
          <div className={`admin-year-context${archiveMode ? ' is-archive' : ''}`}>
            <strong>{selectedAcademicYear || 'Güncel'} akademik yıl</strong>
            <span>
              {archiveMode
                ? 'Arşiv görünümü: ders ve ders içeriği değiştirilemez. Kullanıcı, rol, şifre ve talep yönetimi kullanılabilir.'
                : 'Ders, atama ve içerik işlemleri bu dönem için uygulanır.'}
            </span>
          </div>
          {activeTab === 'ebs' && <EBS />}
          {activeTab === 'akademisyen' && <AkademisyenTanimla />}
          {activeTab === 'akademisyen-toplu' && <AkademisyenTopluEkle />}
          {activeTab === 'auto-assign-courses' && <AutoAssignCourses />}
          {activeTab === 'academic-year' && <AdminAcademicYearAdvance />}
          {activeTab === 'term-outcomes' && <AdminTermOutcomeFinalization />}
          {activeTab === 'feedback' && (
            <div className="admin-feedback-container">
              <h2>{t('admin.requests')}</h2>
              {loadingTalepler && <div className="loading-message">{t('admin.loading')}</div>}
              {errorTalepler && <div className="error-message">{errorTalepler}</div>}
              {!loadingTalepler && !errorTalepler && (
                <div className="admin-feedback-table-container">
                  <table className="admin-feedback-table">
                  <thead>
                    <tr>
                      <th>{t('admin.id')}</th>
                      <th>{t('admin.title')}</th>
                      <th>{t('admin.description')}</th>
                      <th className="table-cell">{t('admin.userId')}</th>
                      <th className="table-cell">{t('admin.userName')}</th>
                      <th className="table-cell">{t('admin.date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {talepler.length === 0 ? (
                      <tr><td colSpan={6} className="empty-table-message">{t('admin.noRecords')}</td></tr>
                    ) : (
                      talepler.map((talep, idx) => {
                        const isRead = talep.is_read;
                        const rowClass = `table-row clickable ${isRead ? 'read' : 'unread'} ${idx % 2 === 0 ? 'even' : 'odd'}`;
                        return (
                          <tr
                            key={talep.id}
                            className={rowClass}
                            onClick={async () => {
                              if (!isRead) {
                                await fetch(API_ENDPOINTS.TALEP_OKUNDU, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ talep_id: talep.id })
                                });
                                setTalepler(prev => prev.map(t => t.id === talep.id ? { ...t, is_read: true } : t));
                              }
                              handleTalepClick(talep);
                            }}
                          >
                            <td className="table-cell table-cell-with-indicator">
                              {!isRead && <span className="unread-indicator"></span>}
                              {talep.id}
                            </td>
                            <td className="table-cell">{talep.title}</td>
                            <td className="table-cell">{talep.description}</td>
                            <td className="table-cell">{talep.user_id || '-'}</td>
                            <td className="table-cell">{talep.user_name || '-'}</td>
                            <td className="table-cell">{talep.created_at ? new Date(talep.created_at).toLocaleString() : '-'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {activeTab === 'reset' && (
            <div className="admin-reset-container">
              <h2>{t('admin.passwordReset')}</h2>
              {loadingReset && <div className="loading-message">{t('admin.loading')}</div>}
              {resetError && <div className="error-message">{resetError}</div>}
              {!loadingReset && !resetError && (
                <div className="admin-reset-table-container">
                  <table className="admin-reset-table">
                  <thead>
                    <tr>
                      <th>{t('admin.email')}</th>
                      <th>{t('admin.date')}</th>
                      <th>{t('admin.operations')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resetRequests.length === 0 ? (
                      <tr><td colSpan={3} className="empty-table-message">{t('admin.noResetRequests')}</td></tr>
                    ) : (
                      resetRequests.map((talep, idx) => {
                        const match = talep.description && talep.description.match(/([\w.-]+@[\w.-]+\.[A-Za-z]{2,})/);
                        const email = match ? match[0] : '-';
                        const isRead = talep.is_read;
                        return (
                          <tr
                            key={talep.id}
                            className={`${isRead ? 'read' : 'unread'}`}
                          >
                            <td className="table-cell-with-indicator">
                              {!isRead && <span className="unread-indicator"></span>}
                              {email}
                            </td>
                            <td>{talep.created_at ? new Date(talep.created_at).toLocaleString() : '-'}</td>
                            <td>
                              <button onClick={async () => {
                                if (!isRead) {
                                  await fetch(API_ENDPOINTS.TALEP_OKUNDU, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ talep_id: talep.id })
                                  });
                                  setResetRequests(prev => prev.map(t => t.id === talep.id ? { ...t, is_read: true } : t));
                                }
                                handleResetClick(talep);
                              }} className="reset-password-btn">{t('admin.resetTitle')}</button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  </table>
                </div>
              )}
              {selectedReset && (
                <div className="reset-modal-overlay" onClick={() => setSelectedReset(null)}>
                  <div
                    className="reset-modal-content"
                    onClick={e => e.stopPropagation()}
                  >
                    <button className="reset-modal-close-btn" onClick={() => setSelectedReset(null)}>&times;</button>
                    <h3 className="reset-modal-title">{t('admin.resetTitle')}</h3>
                    <form onSubmit={handleResetSubmit} className="reset-form">
                      <div className="form-field">
                        <label className="form-label">{t('admin.email')}</label>
                        <input type="email" value={resetEmail} disabled className="form-input" />
                      </div>
                      <div className="form-field">
                        <label className="form-label">{t('admin.newPassword')}</label>
                        <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="form-input" required />
                      </div>
                      {resetResult && <div className={`reset-result-message ${resetResult.includes('güncellendi') || resetResult.includes('updated') ? 'success' : 'error'}`}>{resetResult}</div>}
                      <button type="submit" disabled={resetLoading} className="reset-submit-btn"> {resetLoading ? t('common.loading') : t('admin.updateAndSend')} </button>
                    </form>
                  </div>

                </div>
              )}
            </div>
          )}
          {activeTab === 'role-assignment' && (
            <RoleAssignment />
          )}
          {activeTab === 'active-users' && (
            <ActiveUsers />
          )}
          {selectedTalep && (
            <div className="modal-overlay" onClick={() => setSelectedTalep(null)}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={() => setSelectedTalep(null)}>&times;</button>
                <h3 className="response-modal-title">{t('admin.requestDetail')}</h3>
                <div className="response-detail-item"><b>{t('admin.title')}:</b> {selectedTalep.title}</div>
                <div className="response-detail-item"><b>{t('admin.description')}:</b> {selectedTalep.description}</div>
                <div className="response-detail-item"><b>{t('admin.user')}</b> {selectedTalep.user_name || '-'}</div>
                <div className="response-detail-item"><b>{t('admin.dateLabel')}</b> {selectedTalep.created_at ? new Date(selectedTalep.created_at).toLocaleString() : '-'}</div>
                <hr className="response-divider"/>
                <h4>{t('requests.replies')}</h4>
                {loadingYanit ? <div className="loading-message">{t('admin.loading')}</div> : (
                  yanitlar.length === 0 ? <div className="no-responses">{t('requests.noReply')}</div> : (
                    <ul className="response-list">
                      {yanitlar.map(y => (
                        <li key={y.id} className="response-item">
                          <div className="response-text">{y.yanit}</div>
                          <div className="response-date">{y.created_at ? new Date(y.created_at).toLocaleString() : ''}</div>
                        </li>
                      ))}
                    </ul>
                  )
                )}
                {/* Yanıt yazma alanı sadece admin için */}
                {isAdmin && (
                  <form onSubmit={handleYanitSubmit} className="response-form">
                    <textarea
                      value={yanitText}
                      onChange={e => setYanitText(e.target.value)}
                      rows={3}
                      placeholder={t('admin.replyPlaceholder')}
                      className="response-textarea"
                    />
                    {yanitError && <div className="response-error">{yanitError}</div>}
                    {yanitSuccess && <div className="response-success">{yanitSuccess}</div>}
                    <button type="submit" className="response-submit-btn">{t('admin.reply')}</button>
                  </form>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminPanel;
